import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Bell } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { NotificationItem } from "./NotificationItem";
import { useNotificationSubscription } from "./useNotificationSubscription";

import { log } from "@/utils/log";

export const NotificationDropdown = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Get current user
  const { data: session } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      return session;
    },
  });

  // Set up real-time subscription
  useNotificationSubscription(session?.user?.id);

  const { data: notifications } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('notifications')
        .select(`
          *,
          actor:actor_id (
            id,
            username,
            avatar_url
          )
        `)
        .eq('user_id', user.id)
        .is('read_at', null)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        log.error('Error fetching notifications:', error);
        throw error;
      }
      
      return data;
    },
  });

  const { data: unreadCount } = useQuery({
    queryKey: ['notifications-unread'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .is('read_at', null);

      if (error) throw error;
      return count || 0;
    },
  });

  const handleNotificationClick = async (notification: any) => {
    try {
      setIsOpen(false);

      // Mark as read if not already read
      if (!notification.read_at) {
        const { error: updateError } = await supabase
          .from('notifications')
          .update({ read_at: new Date().toISOString() })
          .eq('id', notification.id);

        if (updateError) throw updateError;

        // Optimistically update the notifications list
        queryClient.setQueryData(['notifications'], (oldData: any[] | undefined) => {
          if (!oldData) return [];
          return oldData.filter(n => n.id !== notification.id);
        });

        // Update the unread count
        queryClient.setQueryData(['notifications-unread'], (oldCount: number | undefined) => {
          return Math.max((oldCount || 0) - 1, 0);
        });
      }

      // Navigate based on notification type
      switch (notification.type) {
        case 'message':
          navigate(`/messages?user=${notification.actor_id}`);
          break;
        case 'like':
        case 'follow':
        case 'comment':
        default:
          if (notification.actor_id) {
            navigate(`/profile/${notification.actor_id}`);
          }
          break;
      }
    } catch (error) {
      log.error('Error handling notification:', error);
      toast({
        title: "Error",
        description: "Failed to process notification",
        variant: "destructive"
      });
    }
  };

  // Group notifications by type
  const groupedNotifications = notifications?.reduce((acc: any, notification: any) => {
    const type = notification.type;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(notification);
    return acc;
  }, {});

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount ? (
            <span className="absolute -top-1 -right-1 bg-secondary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {unreadCount}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        {!notifications?.length ? (
          <div className="p-4 text-center text-muted-foreground">
            No notifications
          </div>
        ) : (
          Object.entries(groupedNotifications || {}).map(([type, notifications]: [string, any[]]) => (
            <div key={type}>
              <div className="px-4 py-2 text-sm font-semibold text-muted-foreground bg-muted/50">
                {type.charAt(0).toUpperCase() + type.slice(1)}s
              </div>
              {notifications.map((notification: any) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={handleNotificationClick}
                />
              ))}
            </div>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
